import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { CalendarDays, Loader2, MapPin, Users } from "lucide-react";
import { formatFullName } from "../../lib/utils";
import { getImageUrl } from "../../lib/coachUtils";
import { useLanguage } from "../../contexts/LanguageContext";

const initials = (name) =>
	(name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join("");

/**
 * The "N athletes" pill shown on a race. Always a button: wherever the headcount
 * appears, tapping it must open the roster (`CompetitionParticipantsDialog`).
 */
export function SharedAthletesBadge({ count, onClick, className = "" }) {
	const { t } = useLanguage();
	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				onClick?.();
			}}
			aria-label={t("competitionParticipantsOpen")}
			title={t("competitionParticipantsOpen")}
			className="shrink-0"
		>
			<Badge
				className={`rounded-sm border-0 text-[10px] cursor-pointer ${
					className || "bg-acid/20 text-acid hover:bg-acid/30"
				}`}
			>
				<Users className="w-3 h-3 mr-1" />
				{count}
			</Badge>
		</button>
	);
}

/**
 * Roster of the athletes attending the same race (same normalised name|date|city).
 * Shared by the coach hub, the single-athlete race manager and the athlete profile:
 * the caller only provides the already-scoped list, never a visibility rule.
 */
export function CompetitionParticipantsDialog({
	open,
	onOpenChange,
	event,
	athletes,
	loading = false,
}) {
	const { t, language } = useLanguage();
	const rows = athletes || [];
	const eventDate = event?.date
		? new Date(event.date).toLocaleDateString(
				language === "it" ? "it-IT" : "en-US",
				{ day: "numeric", month: "short", year: "numeric" },
			)
		: null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-card border-border rounded-sm max-w-sm w-[calc(100vw-2rem)]">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle className="font-heading">
						{t("competitionParticipantsTitle")}
					</DialogTitle>
				</DialogHeader>
				{event && (
					<div className="rounded-sm border border-border bg-background p-3">
						<p className="font-heading text-sm truncate">{event.name}</p>
						<div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
							{eventDate && (
								<span className="flex items-center gap-1">
									<CalendarDays className="w-3 h-3" />
									{eventDate}
								</span>
							)}
							{event.location && (
								<span className="flex items-center gap-1">
									<MapPin className="w-3 h-3" />
									{event.location}
								</span>
							)}
						</div>
					</div>
				)}
				{loading ? (
					<div className="flex items-center justify-center py-8 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin mr-2" />
						{t("loading")}
					</div>
				) : rows.length === 0 ? (
					<p className="text-sm text-muted-foreground py-4 text-center">
						{t("competitionParticipantsEmpty")}
					</p>
				) : (
					<ScrollArea className="max-h-[45vh] pr-3">
						<div className="space-y-1.5">
							{rows.map((athlete) => {
								const fullName = formatFullName(athlete) || "—";
								return (
									<div
										key={athlete.user_id}
										className="flex items-center gap-2 p-2 rounded-sm bg-background border border-border"
									>
										<Avatar className="h-7 w-7 border border-border">
											{athlete.picture ? (
												<AvatarImage
													src={getImageUrl(athlete.picture)}
													alt={fullName}
												/>
											) : null}
											<AvatarFallback className="text-[10px] bg-secondary">
												{initials(fullName)}
											</AvatarFallback>
										</Avatar>
										<span className="text-sm truncate flex-1">{fullName}</span>
										{athlete.is_self && (
											<Badge className="rounded-sm border-0 text-[10px] bg-volt/20 text-volt shrink-0">
												{t("competitionParticipantsYou")}
											</Badge>
										)}
									</div>
								);
							})}
						</div>
					</ScrollArea>
				)}
			</DialogContent>
		</Dialog>
	);
}
